// The app's data, generated from .appfactory/product-plan.json.
//
// EDITED BY HAND in one respect, and only one: the demo rows come from
// `src/lib/demo-rows.js` instead of being inlined. The generator names them
// `${Entity} ${n}` — "Task 1", "Task 2", "Task 3" — and those are what a store
// screenshot photographs. Everything else here is as generated, so re-applying
// the plan and pasting this import back is a small job rather than a merge.
//
// Deliberately the same shape the chassis shipped: plain `ref`s in a module,
// seeded through `demoData` so /demo shows rows and the real app starts
// empty. A generated app should not introduce a state library its author
// never asked for — and `demoData` is what makes the QA `empty` fixture
// actually produce an empty screen rather than a populated one.

import { ref, computed, watch, toRaw } from 'vue';
import { demoData } from './core/demo.js';
import { records } from './core/storage.js';
import { DEMO_TASKS, DEMO_COMPLETIONS } from './lib/demo-rows.js';

// Rows survive a reload.
//
// `core/storage.js` is an IndexedDB record store the chassis has always
// shipped, and the generated store used to ignore it completely — a plain
// `ref([])` that lost everything the moment the page reloaded. An app that
// cannot keep what you typed into it is not working, however well it renders.
//
// Demo rows are NOT persisted. They have negative ids and exist for /demo and
// for QA fixtures; writing them to the real store would put sample customers
// in somebody's actual queue.
const persist = (row) => (Number(row?.id) > 0 ? records.put(toRaw(row)) : null);

// ONE id sequence for every entity, because there is one store.
//
// `records` is a single IndexedDB object store keyed on `id`. Giving each
// entity its own counter starting at 1 meant the first order written
// overwrote the first customer, and removeOrder(1) deleted them — silent
// cross-entity data loss that only appears once an app has two entities.
//
// Seeded from the highest id already stored, so it also survives a reload:
// restarting at 1 would overwrite the first row ever added.
let seq = 0;
const nextId = () => (seq += 1);

// Everything hydrates from ONE read, and nothing may allocate an id before
// it finishes. A row added while this was still in flight took id 1,
// overwrote a stored record, and was then dropped from the screen when the
// hydrated rows replaced the list.
const ready = records.all().then((rows) => {
  seq = rows.reduce((n, r) => Math.max(n, Number(r.id) || 0), 0);
  return rows;
}).catch(() => []);   // no IndexedDB (SSR, private mode) — stay in memory

/** Resolves once stored rows are loaded. Await it before writing. */
export const storeReady = ready;

/** An infrequent thing the person does — changing the air filter, cleaning the gutters, descaling the coffee machine. Holds the definition only; when it was actually done lives in Completion. */
export const tasks = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededTask = demoData(DEMO_TASKS);
watch(seededTask, (rows) => {
  if (rows.length && !tasks.value.length) tasks.value = [...rows];
}, { immediate: true });

export const tasksReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'task');
  if (mine.length) tasks.value = mine;
  return tasks.value;
});

export function addTask(input = {}) {
  const row = { id: nextId(), entity: 'task',
    name: input.name ?? '',
    category: input.category ?? '',
    intervalMonths: input.intervalMonths ?? 0,
    notes: input.notes ?? '',
    createdAt: input.createdAt ?? new Date().toISOString(),
    archived: input.archived ?? false,
  };
  tasks.value = [row, ...tasks.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveTask(row) {
  tasks.value = tasks.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeTask(id) {
  tasks.value = tasks.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findTask = (id) =>
  tasks.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentTask = computed(() => tasks.value.slice(0, 3));
export const tasksTitle = (row) => row?.name;

/** One recorded occasion of doing a task. Marking done writes one of these; the history on the detail screen is the list of them. */
export const completions = ref([]);

// Sample rows at /demo, nothing in the real app. The QA `empty`
// fixture makes demoData return [] so an empty state can be seen.
const seededCompletion = demoData(DEMO_COMPLETIONS);
watch(seededCompletion, (rows) => {
  if (rows.length && !completions.value.length) completions.value = [...rows];
}, { immediate: true });

export const completionsReady = ready.then((rows) => {
  const mine = rows.filter((r) => r.entity === 'completion');
  if (mine.length) completions.value = mine;
  return completions.value;
});

export function addCompletion(input = {}) {
  const row = { id: nextId(), entity: 'completion',
    task: input.task ?? '',
    doneAt: input.doneAt ?? new Date().toISOString(),
    note: input.note ?? '',
    loggedAt: input.loggedAt ?? new Date().toISOString(),
  };
  completions.value = [row, ...completions.value];
  persist(row);
  return row;
}

/** Save an edit made in place. Call it after changing a row's fields. */
export function saveCompletion(row) {
  completions.value = completions.value.map((r) => (String(r.id) === String(row.id) ? row : r));
  persist(row);
  return row;
}

export function removeCompletion(id) {
  completions.value = completions.value.filter((r) => String(r.id) !== String(id));
  if (Number(id) > 0) records.delete(Number(id));
}

export const findCompletion = (id) =>
  completions.value.find((r) => String(r.id) === String(id)) ?? null;

export const recentCompletion = computed(() => completions.value.slice(0, 3));
export const completionsTitle = (row) => row?.id;
